import type { ComponentProps } from "react";
import type Icon from "@/components/Icon";

export type RecordEntityKind = "person" | "event" | "facility" | "section" | "job" | "generic";

export type RecordListItemFields = {
  title: string;
  subtitle?: string | null;
  meta?: string | null;
  href?: string | null;
  imageUrl?: string | null;
  kind?: RecordEntityKind | null;
  facilityName?: string | null;
  facilityHref?: string | null;
  location?: string | null;
  links?: { label: string; href: string; description?: string | null }[] | null;
  selectValue?: string | null;
};

export type FacilityLocationDisplay = {
  facilityName?: string;
  facilityHref?: string;
  location?: string;
  fallbackSubtitle?: string;
};

const INTERNAL_META_PATTERNS: RegExp[] = [
  /\bCode:\s*P?\d+\s*/gi,
  /\bEvent\s+code:\s*\S+\s*/gi,
  /\b(?:Section|Facility|Event)\s+ID:\s*[\w-]+\s*/gi,
  /(?<!PGA )(?<!Member )\bID:\s*[\w-]+\s*/gi,
  /\bslug\s+[\w-]+\s*/gi,
  /\(slug\s+[\w-]+\)/gi
];

function normalizeMetaSeparators(text: string): string {
  return text
    .replace(/\s*\|\s*/g, " · ")
    .replace(/\s*·\s*·\s*/g, " · ")
    .replace(/^[\s·|]+|[\s·|]+$/g, "")
    .trim();
}

/** Strip internal identifiers (codes, IDs, slugs) from card meta lines. */
export function sanitizeCardMeta(meta?: string | null): string | undefined {
  if (!meta?.trim()) {
    return undefined;
  }

  let cleaned = meta.trim();
  for (const pattern of INTERNAL_META_PATTERNS) {
    cleaned = cleaned.replace(pattern, "");
  }

  cleaned = cleaned.replace(/\bStatus:\s*/gi, "").trim();
  cleaned = normalizeMetaSeparators(cleaned);
  cleaned = cleaned.replace(/^(?:Section|Facility|Event)$/i, "").trim();

  return cleaned || undefined;
}

export function inferRecordEntityKind(href?: string | null, explicitKind?: RecordEntityKind | null): RecordEntityKind {
  if (explicitKind && explicitKind !== "generic") {
    return explicitKind;
  }

  if (!href) {
    return explicitKind ?? "generic";
  }

  if (href.startsWith("/people/")) {
    return "person";
  }
  if (href.startsWith("/events/")) {
    return "event";
  }
  if (href.startsWith("/facilities/")) {
    return "facility";
  }
  if (href.startsWith("/sections/")) {
    return "section";
  }
  if (href.startsWith("/jobs/")) {
    return "job";
  }

  return explicitKind ?? "generic";
}

export function entityKindIcon(kind: RecordEntityKind): ComponentProps<typeof Icon>["name"] {
  switch (kind) {
    case "person":
      return "person";
    case "event":
      return "event";
    case "facility":
      return "location_city";
    case "section":
      return "groups";
    case "job":
      return "work";
    default:
      return "article";
  }
}

export function entityKindEyebrow(kind: RecordEntityKind): string | undefined {
  switch (kind) {
    case "event":
      return "Event";
    case "facility":
      return "Facility";
    case "section":
      return "Section";
    case "job":
      return "Job post";
    default:
      return undefined;
  }
}

export function resolveFacilityLocationDisplay(item: RecordListItemFields): FacilityLocationDisplay {
  const kind = inferRecordEntityKind(item.href, item.kind);
  const parsesFacilityLocationSubtitle = kind === "event" || kind === "job";

  let facilityName = item.facilityName?.trim() || undefined;
  let facilityHref = item.facilityHref?.trim() || undefined;
  let location = item.location?.trim() || undefined;

  const facilityLink = item.links?.find((link) => link.href.startsWith("/facilities/"));
  if (!facilityHref && facilityLink) {
    facilityHref = facilityLink.href;
    const linkLabel = facilityLink.label.trim();
    if (linkLabel && linkLabel.toLowerCase() !== "facility") {
      facilityName = facilityName ?? linkLabel;
    }
  }

  if (parsesFacilityLocationSubtitle) {
    if (!facilityName && !location && item.subtitle?.trim()) {
      const parts = item.subtitle
        .split(" · ")
        .map((part) => part.trim())
        .filter(Boolean);
      if (parts.length >= 2) {
        facilityName = parts[0];
        location = parts.slice(1).join(" · ");
      } else if (!facilityName && !location) {
        location = parts[0];
      }
    } else if (!location && item.subtitle?.trim() && facilityName) {
      const subtitle = item.subtitle.trim();
      if (subtitle.localeCompare(facilityName, undefined, { sensitivity: "accent" }) !== 0) {
        location = subtitle;
      }
    }
  }

  return {
    facilityName,
    facilityHref,
    location,
    fallbackSubtitle: !facilityName && !location && !parsesFacilityLocationSubtitle ? item.subtitle?.trim() : undefined
  };
}

export function buildRecordRowDisplay(item: RecordListItemFields): {
  title: string;
  subtitle?: string;
  meta?: string;
  showPgaId: boolean;
  pgaId: string | null;
  facility: FacilityLocationDisplay;
} {
  const meta = sanitizeCardMeta(item.meta);
  const facility = resolveFacilityLocationDisplay(item);
  const showMeta =
    meta && meta !== item.subtitle?.trim() && !item.subtitle?.includes(meta) && !item.title.includes(meta);

  const pgaIdMatch = item.href?.match(/^\/people\/([^/]+)\//);
  const pgaId = pgaIdMatch?.[1] ?? null;

  return {
    title: item.title,
    subtitle: undefined,
    meta: showMeta ? meta : undefined,
    showPgaId: inferRecordEntityKind(item.href, item.kind) === "person" && Boolean(pgaId),
    pgaId,
    facility
  };
}
